import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { MenuItemProps } from '@/components/mui/select';
import { useFetchLaxatives } from '@/hooks/api/careSettings/useFetchLaxatives';
import { LAXATIVE_UNIT_JA } from '@/constants';
import { useTranslation } from 'react-i18next';
import { numBetweenMinMax } from '@/utils/dataFormatUtil';

export interface CareRecordFormPayload {
  residentId: number;
  timestamp: string;
  urinatedToilet: boolean | null;
  toiletType: 'toilet' | null;
  padUrineVolumeType: string | null;
  padType: 'pad' | null;
  defecationVolumeType: string | null;
  hardnessType: string | null;
  toiletVolume: number | null;
  padUrineVolume: number | null;
  isUrgency: boolean | null;
  isLeakOutUrine: boolean | null;
  isLeakOutFeces: boolean | null;
  laxativeId: number | null;
  laxativeAmount: number | null;
  drinkingVolume: number | null;
  staffName: string | null;
  workingTime: number | null;
  memo: string | null;
  diaper: {
    useDefaultDiaper: boolean | null;
    useDefaultPad: boolean | null;
  };
}

export const useCareRecordForm = (onSend: boolean) => {
  const { t } = useTranslation();
  const now = dayjs();
  const { laxatives } = useFetchLaxatives(true, 'asc');

  const [isDisplayAdvancedForm, setIsDisplayAdvancedForm] = React.useState<boolean>(false);
  const [isDisplayPadUrineVolume, setIsDisplayPadUrineVolume] = React.useState<boolean>(false);
  const [isDisplayToiletUrineVolume, setIsDisplayToiletUrineVolume] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Dayjs>(now);
  const [isUrinatedToilet, setisUrinatedToilet] = React.useState<boolean | null>(null);
  const [padUrineVolumeType, setPadUrineVolumeType] = React.useState<string | null>(null);
  const [defecationVolumeType, setDefecationVolumeType] = React.useState<string | null>(null);
  const [hardnessType, setHardnessType] = React.useState<string | null>(null);
  const [isCheckPant, setCheckPant] = React.useState<boolean>(false);
  const [isCheckPad, setCheckPad] = React.useState<boolean>(false);
  const [toiletVolume, setToiletVolume] = React.useState<number | null>(null);
  const [padUrineVolume, setPadUrineVolume] = React.useState<number | null>(null);
  const [isUrgency, setIsUrgency] = React.useState<boolean | null>(null);
  const [isLeakOutUrine, setIsLeakOutUrine] = React.useState<boolean | null>(null);
  const [isLeakOutFeces, setIsLeakOutFeces] = React.useState<boolean | null>(null);
  const [laxativeId, setLaxativeId] = React.useState<MenuItemProps | null>(null);
  const [laxativeAmount, setLaxativeAmount] = React.useState<number | null>(null);
  const [drinkingVolume, setDrinkingVolume] = React.useState<number | null>(null);
  const [staffName, setStaffName] = React.useState<string | null>(null);
  const [workingTime, setWorkingTime] = React.useState<number | null>(5);
  const [memo, setMemo] = React.useState<string | null>(null);

  const laxativeList = React.useMemo(() => {
    if (!laxatives) return [];
    return [
      { key: '-', value: t('notSelected', '未選択') },
      ...laxatives.map((v) => {
        return { key: v.id, value: v.name };
      }),
    ];
  }, [laxatives, t]);

  const isEmptyLaxativeId = React.useMemo(
    () => JSON.stringify(laxativeId) === JSON.stringify(laxativeList[0]),
    [laxativeId, laxativeList]
  );

  const disabledSubmit = React.useMemo(() => {
    if (onSend) return true;
    if (defecationVolumeType && defecationVolumeType !== 'none' && !hardnessType) return true;
    if (isUrinatedToilet === null && padUrineVolumeType === null) {
      return (
        !defecationVolumeType &&
        !isCheckPant &&
        !isCheckPad &&
        !(laxativeId && laxativeAmount) &&
        !drinkingVolume &&
        !isLeakOutUrine &&
        !isLeakOutFeces &&
        !memo
      );
    }
    if (isUrinatedToilet === null || padUrineVolumeType === null) return true;
    return false;
  }, [
    onSend,
    isUrinatedToilet,
    padUrineVolumeType,
    defecationVolumeType,
    hardnessType,
    isCheckPant,
    isCheckPad,
    laxativeId,
    laxativeAmount,
    drinkingVolume,
    isLeakOutUrine,
    isLeakOutFeces,
    memo,
  ]);

  const laxativeUnit = React.useMemo(() => {
    if (!laxatives || !laxativeId || isEmptyLaxativeId) return '';
    const unit = laxatives.find((v) => v.id === laxativeId?.key)?.unit;
    if (!unit) return '';
    return LAXATIVE_UNIT_JA[unit] ?? '';
  }, [laxativeId, laxatives, isEmptyLaxativeId]);

  const isToiletVolumeDisabled = React.useMemo(() => !isUrinatedToilet, [isUrinatedToilet]);
  const isPadVolumeDisabled = React.useMemo(
    () => !padUrineVolumeType || padUrineVolumeType === 'none',
    [padUrineVolumeType]
  );
  const shouldShowToiletVolume = isDisplayToiletUrineVolume;
  const shouldShowPadVolume = isDisplayPadUrineVolume;
  const isUrineMetaDisabled = React.useMemo(
    () => isToiletVolumeDisabled && isPadVolumeDisabled,
    [isToiletVolumeDisabled, isPadVolumeDisabled]
  );

  const handleChangeDate = (newDate: Dayjs) => {
    setDate(newDate);
  };

  const handleChangeTime = (time: Dayjs) => {
    setDate(time);
  };

  const handleChangeRecordButton = () => {
    setIsDisplayAdvancedForm((prev) => !prev);
    setisUrinatedToilet(null);
    setPadUrineVolumeType(null);
    setDefecationVolumeType(null);
    setHardnessType(null);
    setCheckPant(false);
    setCheckPad(false);
    setIsDisplayPadUrineVolume(false);
    setIsDisplayToiletUrineVolume(false);
    setToiletVolume(null);
    setPadUrineVolume(null);
    setIsUrgency(null);
    setIsLeakOutUrine(null);
    setIsLeakOutFeces(null);
    setLaxativeId(null);
    setLaxativeAmount(null);
    setDrinkingVolume(null);
    setStaffName(null);
    setWorkingTime(5);
    setMemo(null);
  };

  const handleChangeIsUrinatedToilet = (data: string | number | boolean | null) => {
    const nextIsUrinatedToilet = data === 'true' ? true : data === 'false' ? false : null;
    setisUrinatedToilet(nextIsUrinatedToilet);
    if (!nextIsUrinatedToilet) {
      setToiletVolume(null);
    }
    const nextIsToiletVolumeDisabled = !nextIsUrinatedToilet;
    if (nextIsToiletVolumeDisabled && isPadVolumeDisabled && isUrgency) {
      setIsUrgency(false);
    }
    setIsDisplayToiletUrineVolume(nextIsUrinatedToilet === true);
  };

  const handleChangePadUrineVolumeType = (data: string | number | boolean | null) => {
    const nextPadUrineVolumeType = data ? String(data) : null;
    setPadUrineVolumeType(nextPadUrineVolumeType);
    if (!nextPadUrineVolumeType || nextPadUrineVolumeType === 'none') {
      setPadUrineVolume(null);
    }
    const nextIsPadVolumeDisabled = !nextPadUrineVolumeType || nextPadUrineVolumeType === 'none';
    if (isToiletVolumeDisabled && nextIsPadVolumeDisabled && isUrgency) {
      setIsUrgency(false);
    }
    setIsDisplayPadUrineVolume(nextPadUrineVolumeType !== 'none' && nextPadUrineVolumeType !== null);
  };

  const handleChangeDefecationVolumeType = (data: string | number | boolean | null) => {
    setDefecationVolumeType(data ? String(data) : null);
  };

  const handleChangeHardnessType = (data: string | number | boolean | null) => {
    setHardnessType(data ? String(data) : null);
  };

  const handleClickPantButton = () => {
    setCheckPant(!isCheckPant);
  };

  const handleClickPadButton = () => {
    setCheckPad(!isCheckPad);
  };

  const handleChangeToiletVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToiletVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangePadUrineVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPadUrineVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeLaxativeId = (data: MenuItemProps) => {
    setLaxativeId(data);
  };

  const handleChangeLaxativeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLaxativeAmount(numBetweenMinMax(+e.target.value, 1, 9999));
  };

  const handleChangeDrinkingVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDrinkingVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeStaffName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStaffName(e.target.value);
  };

  const handleChangeWorkingTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingTime(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeMemo = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemo(e.target.value);
  };

  const handleCheckLeakOutUrine = () => {
    setIsLeakOutUrine(!isLeakOutUrine);
  };

  const handleCheckIsUrgency = () => {
    setIsUrgency(!isUrgency);
  };

  const handleCheckLeakOutFeces = () => {
    setIsLeakOutFeces(!isLeakOutFeces);
  };

  const buildSendData = (residentId: number): CareRecordFormPayload => {
    return {
      residentId,
      timestamp: date.local().utc().format(),
      urinatedToilet: isUrinatedToilet,
      toiletType: isUrinatedToilet === null && padUrineVolumeType === null ? null : 'toilet',
      padUrineVolumeType,
      padType: isUrinatedToilet === null && padUrineVolumeType === null ? null : 'pad',
      defecationVolumeType,
      hardnessType,
      toiletVolume,
      padUrineVolume,
      isUrgency,
      isLeakOutUrine,
      isLeakOutFeces,
      laxativeId: !!laxativeId?.key && !isEmptyLaxativeId ? +laxativeId.key : null,
      laxativeAmount,
      drinkingVolume,
      staffName,
      workingTime,
      memo,
      diaper: {
        useDefaultDiaper: isCheckPant ? true : null,
        useDefaultPad: isCheckPad ? true : null,
      },
    };
  };

  return {
    now,
    date,
    isDisplayAdvancedForm,
    isDisplayPadUrineVolume,
    isDisplayToiletUrineVolume,
    shouldShowToiletVolume,
    shouldShowPadVolume,
    isToiletVolumeDisabled,
    isPadVolumeDisabled,
    isUrineMetaDisabled,
    isUrinatedToilet,
    padUrineVolumeType,
    defecationVolumeType,
    hardnessType,
    isCheckPant,
    isCheckPad,
    toiletVolume,
    padUrineVolume,
    isUrgency,
    isLeakOutUrine,
    isLeakOutFeces,
    laxativeId,
    laxativeAmount,
    drinkingVolume,
    staffName,
    workingTime,
    memo,
    laxativeList,
    isEmptyLaxativeId,
    laxativeUnit,
    disabledSubmit,
    setHardnessType,
    setIsLeakOutUrine,
    setIsLeakOutFeces,
    setLaxativeAmount,
    handleChangeDate,
    handleChangeTime,
    handleChangeRecordButton,
    handleChangeIsUrinatedToilet,
    handleChangePadUrineVolumeType,
    handleChangeDefecationVolumeType,
    handleChangeHardnessType,
    handleClickPantButton,
    handleClickPadButton,
    handleChangeToiletVolume,
    handleChangePadUrineVolume,
    handleChangeLaxativeId,
    handleChangeLaxativeAmount,
    handleChangeDrinkingVolume,
    handleChangeStaffName,
    handleChangeWorkingTime,
    handleChangeMemo,
    handleCheckLeakOutUrine,
    handleCheckIsUrgency,
    handleCheckLeakOutFeces,
    buildSendData,
  };
};

export type CareRecordFormHook = ReturnType<typeof useCareRecordForm>;
